import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Database, HelpCircle, ChevronRight, Clock, CheckCircle2, RotateCcw, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AppHeader } from '@/components/AppHeader';
import { listProjects, unarchiveProject } from '@/lib/api/projects';
import { roleCanEdit } from '@/hooks/useProjectRole';
import type { ProjectMemberRole } from '@/types/backend';
import { formatDistanceToNow, isValid, parseISO } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

interface Project {
  id: string;
  name: string;
  client: string;
  createdAt: string;
  rfiStatus: { total: number; received: number };
  qaStatus: { total: number; answered: number };
  databookStatus: 'not_started' | 'in_progress' | 'complete';
  lastActivity: string;
  currentUserRole: ProjectMemberRole;
}

const getDatabookStatusLabel = (status: Project['databookStatus']) => {
  switch (status) {
    case 'not_started': return 'Not Started';
    case 'in_progress': return 'In Progress';
    case 'complete': return 'Complete';
  }
};

const formatLastActivity = (iso: string) => {
  const d = parseISO(iso);
  if (!isValid(d)) return iso;
  return formatDistanceToNow(d, { addSuffix: true });
};

const ArchivedProjects = () => {
  const navigate = useNavigate();
  const [projectToUnarchive, setProjectToUnarchive] = useState<Project | null>(null);
  const [isUnarchiving, setIsUnarchiving] = useState(false);

  const { data: backendProjects, isLoading, isError, refetch } = useQuery({
    queryKey: ['projects', 'archived'],
    queryFn: () => listProjects(true),
  });

  const projects: Project[] = (backendProjects ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    client: 'Client', // backend doesn't provide client yet
    createdAt: p.created_at,
    rfiStatus: { total: 24, received: 0 }, // mock until backend provides status
    qaStatus: { total: 0, answered: 0 }, // mock until backend provides status
    databookStatus: 'not_started' as const,
    lastActivity: formatLastActivity(p.updated_at),
    currentUserRole: p.current_user_role,
  }));

  const handleProjectClick = (projectId: string) => {
    navigate(`/project/${projectId}`);
  };

  const handleUnarchiveProject = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setProjectToUnarchive(project);
  };

  const confirmUnarchive = async () => {
    if (!projectToUnarchive) return;

    setIsUnarchiving(true);
    try {
      await unarchiveProject(projectToUnarchive.id);
      toast.success(`"${projectToUnarchive.name}" has been unarchived`);
      setProjectToUnarchive(null);
      // Refetch archived projects list to reflect the change (unarchived project will disappear)
      refetch();
    } catch (error: any) {
      console.error('Failed to unarchive project:', error);
      const errorMessage = error?.message || 'Failed to unarchive project. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsUnarchiving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="container mx-auto px-8 py-10">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-10">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-foreground tracking-tight mb-1">
                Archived Projects
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                View and restore your archived projects
              </p>
              {isError && (
                <p className="text-xs text-muted-foreground mt-1">
                  Failed to load archived projects.
                </p>
              )}
            </div>
          </div>

          {/* Projects grid */}
          <div className="space-y-4">
            {projects.map((project) => {
              return (
                <Card 
                  key={project.id} 
                  className="border-border/50 hover:border-border transition-colors cursor-pointer group"
                  onClick={() => handleProjectClick(project.id)}
                >
                  <CardContent className="p-6">
                    {/* Top row - Project name, status badge, activity & arrow */}
                    <div className="flex items-start justify-between gap-4 mb-1">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <h3 className="text-base font-semibold text-foreground tracking-tight truncate">
                          {project.name}
                        </h3>
                        {project.databookStatus === 'complete' && (
                          <span className="flex items-center gap-1 text-xs text-foreground/60 border border-border/50 px-2 py-0.5 rounded shrink-0">
                            <CheckCircle2 className="w-3 h-3" />
                            Complete
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>{project.lastActivity}</span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground/50 group-hover:text-foreground/50 transition-colors" />
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-4">{project.client}</p>

                    {/* Bottom row - Status indicators and Unarchive button */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded border border-border/40 bg-muted/30 flex items-center justify-center">
                            <FileText className="w-3.5 h-3.5 text-foreground/60" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">RFI</p>
                            <p className="text-sm font-medium text-foreground">
                              {project.rfiStatus.received}/{project.rfiStatus.total}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded border border-border/40 bg-muted/30 flex items-center justify-center">
                            <HelpCircle className="w-3.5 h-3.5 text-foreground/60" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Q&A</p>
                            <p className="text-sm font-medium text-foreground">
                              {project.qaStatus.answered}/{project.qaStatus.total}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded border border-border/40 bg-muted/30 flex items-center justify-center">
                            <Database className="w-3.5 h-3.5 text-foreground/60" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Databook</p>
                            <p className="text-sm font-medium text-foreground">
                              {getDatabookStatusLabel(project.databookStatus)}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Unarchive button - bottom right */}
                      {roleCanEdit(project.currentUserRole) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                          onClick={(e) => handleUnarchiveProject(project, e)}
                        >
                          <RotateCcw className="w-4 h-4 mr-1.5" />
                          Unarchive
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Empty state (hidden when projects exist) */}
          {!isLoading && projects.length === 0 && (
            <Card className="border-border/50 border-dashed">
              <CardContent className="py-16 text-center">
                <div className="w-14 h-14 mx-auto mb-5 rounded-full border border-border/40 bg-muted/30 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-foreground/40" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2 tracking-tight">No archived projects</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Projects you archive from the home page will appear here. You can restore them at any time.
                </p>
                <Button 
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Projects
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Unarchive Confirmation Dialog */}
      <AlertDialog open={!!projectToUnarchive} onOpenChange={() => setProjectToUnarchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unarchive Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unarchive "{projectToUnarchive?.name}"? It will be restored to your main projects list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnarchiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmUnarchive} disabled={isUnarchiving}>
              {isUnarchiving ? 'Unarchiving...' : 'Unarchive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ArchivedProjects;
